import { type FetchResult, useMutation, useQuery } from '@apollo/client';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, Container, CssBaseline, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type MouseEvent, useContext, useState } from 'react';
import useStyles from 'src/AkomiLoginPage/AkomiLoginForm/AkomiLoginFormStyles';
import { tokenAuth } from 'src/api/auth';
import { allConfigs } from 'src/api/configurations';
import { ENABLE_OAUTH } from 'src/constants';
import AuthContext from 'src/context/AuthContext';
import { getTokenExpirationMinutes, setTokenAndExpire } from 'src/helpers/authHelper';
import { cleanPayload } from 'src/helpers/common';
import { OAUTHLogin } from 'src/oauth';
import sanitize from 'src/sanitize';
import * as Yup from 'yup';

interface LoginFormValues {
  username: string;
  password: string;
}

interface TokenAuthPayload {
  exp: number;
}

interface TokenAuthResponse {
  tokenAuth: {
    token: string;
    refreshExpiresIn: number;
    payload: TokenAuthPayload;
  };
}

interface ConfigNode {
  id: string;
  optionKey: string;
  optionValue: string;
  optionScope: string;
}

interface ConfigEdge {
  node: ConfigNode;
}

interface AllConfigurationsResponse {
  allConfigurations: {
    edges: ConfigEdge[];
  };
}

interface CleanedConfig {
  id?: string;
  optionKey?: string;
  optionValue?: string;
  optionScope?: string;
}

const AkomiLoginForm: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [sendAuthRequest] = useMutation<TokenAuthResponse>(tokenAuth);
  const [passwordVisibility, setPasswordVisibility] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string>('');

  const { data: loginMessage } = useQuery<AllConfigurationsResponse>(allConfigs, {
    variables: {
      optionKey: 'login_message',
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: oauthEnabled } = useQuery<AllConfigurationsResponse>(allConfigs, {
    variables: {
      optionKey: ENABLE_OAUTH,
    },
    fetchPolicy: 'cache-and-network',
  });

  const oauthEnabledCleaned: CleanedConfig[] = cleanPayload(oauthEnabled);

  const formik = useFormik<LoginFormValues>({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema: Yup.object({
      username: Yup.string().required('This field is required'),
      password: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values: LoginFormValues, _helpers: FormikHelpers<LoginFormValues>): Promise<void> => {
      const { username, password } = values;
      sendAuthRequest({
        variables: { username, password },
      })
        .then((response: FetchResult<TokenAuthResponse>) => {
          if (response.data) {
            setTokenAndExpire(
              response.data.tokenAuth.token,
              response.data.tokenAuth.refreshExpiresIn,
              response.data.tokenAuth.payload.exp,
            );
            const tokenExp = getTokenExpirationMinutes(response.data.tokenAuth.payload.exp);
            authContext.setTokenExpiration?.(tokenExp);
            authContext.setHasToken?.(true);
            navigate({ to: window.location.search });
          }
        })
        .catch((error: Error) => {
          console.log(error);
          setAuthError('Please enter valid credentials');
        });
    },
  });

  const inValidLicenseVerbiage = (): string => {
    if (authContext.validLicense === false) {
      return 'Invalid license detected';
    }
    return '';
  };

  const togglePasswordVisibility = (): void => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  const onForgotPasswordClick = (): void => {
    navigate({ to: '/reset-password' });
  };

  return (
    <Container className={classes.formContainer} component="main">
      <CssBaseline />
      <form className={classes.form} onSubmit={formik.handleSubmit}>
        <Typography component="h1" variant="h1" className={classes.formTitle}>
          Log In
        </Typography>
        <div className={classes.formElements}>
          <TextField
            className={classes.formInput}
            label="Username"
            variant="outlined"
            fullWidth
            {...formik.getFieldProps('username')}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
            FormHelperTextProps={{
              classes: {
                root: classes.helperText,
              },
            }}
          />
          <TextField
            label="Password"
            type={passwordVisibility ? 'text' : 'password'}
            variant="outlined"
            className={classes.formInput}
            fullWidth
            {...formik.getFieldProps('password')}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            FormHelperTextProps={{
              classes: {
                root: classes.helperText,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={togglePasswordVisibility}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                    size="large"
                  >
                    {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={Boolean(formik.errors.username) || Boolean(formik.errors.password)}
            className={classes.submitButton}
          >
            SIGN IN
          </Button>
        </div>
        {oauthEnabledCleaned[0]?.optionValue === '1' && <OAUTHLogin />}
        <Typography className={classes.loginError} color="error">
          {authError || inValidLicenseVerbiage()}
        </Typography>
      </form>
      <div className={classes.forgotPassword}>
        <span>forgot password?</span>
        <Button onClick={onForgotPasswordClick} variant="contained" color="primary">
          Reset Password
        </Button>
      </div>
      {loginMessage?.allConfigurations.edges[0]?.node.optionValue && (
        <Typography
          className={classes.loginMessage}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is sanitized via sanitize() helper before rendering
          dangerouslySetInnerHTML={sanitize(`${loginMessage.allConfigurations.edges[0].node.optionValue}`)}
        />
      )}
    </Container>
  );
};

export default AkomiLoginForm;
